/**
 * features/setup/TeacherSwap.tsx
 * ------------------------------------------------------------------
 * Bulk mid-year teacher reassignment for one SUBJECT + CLASS: move
 * every student currently taught by teacher A to teacher B.
 *
 * WHY THIS IS SAFE re: history — attribution of PRIOR-TERM marks and
 * comments is frozen at save time via SubjectMarkEntry.teacherId (see
 * the stamp in MarkEntry + seed/backfill-mark-teacherId.cjs). This tool
 * only rewrites the LIVE link student.subjectTeachers[subjectId]; it
 * NEVER touches reportMarks or reportComments. Read paths prefer the
 * stamped teacher, so a swap changes future/current attribution while
 * past terms keep the teacher who actually taught them.
 *
 * Eligibility: the destination teacher must actually teach the subject
 * in the class (lib/teacherLink.teacherTeachesSubjectInClass), so a swap
 * can't point marks at someone who doesn't teach the group.
 *
 * Gated on canManageStudents (same custodians as the student roster).
 * The write is a chunked writeBatch of set(..., { merge: true }) on
 * subjectTeachers, so only the one subject key is touched per student.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { doc, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS, useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getTeachersForSubjectInClass } from '@/lib/teacherLink';
import { stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import { Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { ArrowLeftRight, AlertTriangle, Check, Users } from 'lucide-react';

const CHUNK = 400;

export default function TeacherSwap() {
  const { students, teachers, subjects, classes, loading } = useData();
  const { userData, permissions } = useAuth();

  const [subjectId, setSubjectId] = useState('');
  const [classId, setClassId] = useState('');
  const [fromTeacherId, setFromTeacherId] = useState('');
  const [toTeacherId, setToTeacherId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const teacherName = (id?: string) =>
    teachers.find((t) => t.id === id)?.displayName ?? '—';

  const cls = useMemo(
    () => classes.find((c) => c.id === classId),
    [classes, classId],
  );

  // Classes that offer the selected subject (some student is enrolled in it).
  const classesForSubject = useMemo(() => {
    if (!subjectId) return [];
    const ids = new Set(
      students
        .filter((s) => s.subjects?.includes(subjectId))
        .map((s) => s.currentClass),
    );
    return classes
      .filter((c) => ids.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [students, classes, subjectId]);

  // Students in the class enrolled in the subject.
  const classSubjectStudents = useMemo(() => {
    if (!subjectId || !classId) return [];
    return students.filter(
      (s) => s.currentClass === classId && s.subjects?.includes(subjectId),
    );
  }, [students, subjectId, classId]);

  // Current (from) teachers actually assigned to these students for the subject.
  const currentTeacherIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of classSubjectStudents) {
      const tid = s.subjectTeachers?.[subjectId];
      if (tid) ids.add(tid);
    }
    return [...ids];
  }, [classSubjectStudents, subjectId]);

  // Eligible destination teachers: those who teach this subject in this class.
  const eligibleToTeachers = useMemo(() => {
    if (!subjectId || !classId || !cls) return [];
    return getTeachersForSubjectInClass(
      teachers,
      subjectId,
      classId,
      cls.name,
    )
      .filter((t) => t.id !== fromTeacherId)
      .sort((a, b) =>
        (a.displayName ?? '').localeCompare(b.displayName ?? ''),
      );
  }, [teachers, subjectId, classId, cls, fromTeacherId]);

  // The students who will actually move (currently taught by fromTeacher).
  const affected = useMemo(() => {
    if (!fromTeacherId) return [];
    return classSubjectStudents
      .filter((s) => s.subjectTeachers?.[subjectId] === fromTeacherId)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [classSubjectStudents, subjectId, fromTeacherId]);

  const ready =
    !!subjectId &&
    !!classId &&
    !!fromTeacherId &&
    !!toTeacherId &&
    fromTeacherId !== toTeacherId &&
    affected.length > 0;

  // Reset dependent selections when an upstream choice changes.
  function pickSubject(v: string) {
    setSubjectId(v);
    setClassId('');
    setFromTeacherId('');
    setToTeacherId('');
    setConfirming(false);
    setMsg(null);
  }
  function pickClass(v: string) {
    setClassId(v);
    setFromTeacherId('');
    setToTeacherId('');
    setConfirming(false);
    setMsg(null);
  }

  async function applySwap() {
    if (!ready) return;
    setBusy(true);
    setMsg(null);
    try {
      const ids = affected.map((s) => s.id);
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = writeBatch(db);
        for (const sid of ids.slice(i, i + CHUNK)) {
          // Deep-merge ONLY subjectTeachers[subjectId]; merge:true leaves
          // every other subject link (and the rest of the doc) untouched.
          const payload = stripUndefined({
            subjectTeachers: { [subjectId]: toTeacherId },
            updatedAt: new Date().toISOString(),
            updatedBy: userData?.uid,
          });
          batch.set(doc(db, COLLECTIONS.students, sid), payload, {
            merge: true,
          });
        }
        await batch.commit();
      }
      auditFromContext(userData, 'teacher_swap', 'academic', {
        targetId: classId,
        targetType: 'class',
        targetName: `${subjectName(subjectId)} · ${cls?.name ?? ''}`,
        details: {
          subjectId,
          classId,
          fromTeacherId,
          toTeacherId,
          studentsMoved: ids.length,
        },
      });
      setMsg({
        ok: true,
        text: `Moved ${ids.length} student${ids.length === 1 ? '' : 's'} from ${teacherName(fromTeacherId)} to ${teacherName(toTeacherId)}. Prior-term marks and comments are unchanged.`,
      });
      setConfirming(false);
      setFromTeacherId('');
      setToTeacherId('');
    } catch (e) {
      setMsg({ ok: false, text: `Swap failed: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  }

  function subjectName(id: string) {
    return subjects.find((s) => s.id === id)?.name ?? id;
  }

  if (loading) return <Spinner label="Loading…" />;

  if (!permissions.canManageStudents) {
    return (
      <div className="p-6">
        <PageHeader title="Teacher Swap" />
        <Card className="p-6 text-sm text-gray-500">
          You don’t have permission to reassign subject teachers.
        </Card>
      </div>
    );
  }

  const selectCls =
    'w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800';

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title="Teacher Swap"
        subtitle="Move all students of a subject + class from one teacher to another. Prior-term marks and comments keep their original teacher."
      />

      <Card className="p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Subject</label>
            <select
              value={subjectId}
              onChange={(e) => pickSubject(e.target.value)}
              className={selectCls}
            >
              <option value="">Select subject…</option>
              {[...subjects]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Class</label>
            <select
              value={classId}
              onChange={(e) => pickClass(e.target.value)}
              disabled={!subjectId}
              className={selectCls}
            >
              <option value="">Select class…</option>
              {classesForSubject.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">From teacher</label>
            <select
              value={fromTeacherId}
              onChange={(e) => {
                setFromTeacherId(e.target.value);
                setToTeacherId('');
                setConfirming(false);
              }}
              disabled={!classId}
              className={selectCls}
            >
              <option value="">Select current teacher…</option>
              {currentTeacherIds.map((tid) => (
                <option key={tid} value={tid}>
                  {teacherName(tid)}
                </option>
              ))}
            </select>
            {classId && currentTeacherIds.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No students in this class have a teacher set for this subject.
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">To teacher (eligible)</label>
            <select
              value={toTeacherId}
              onChange={(e) => {
                setToTeacherId(e.target.value);
                setConfirming(false);
              }}
              disabled={!fromTeacherId}
              className={selectCls}
            >
              <option value="">Select new teacher…</option>
              {eligibleToTeachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName}
                </option>
              ))}
            </select>
            {fromTeacherId && eligibleToTeachers.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No other teacher is assigned to teach this subject in this
                class. Assign one in Setup → Teachers first.
              </p>
            )}
          </div>
        </div>

        {fromTeacherId && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              <Users size={16} />
              {affected.length} student{affected.length === 1 ? '' : 's'} will
              move
            </div>
            {affected.length === 0 ? (
              <p className="text-xs text-gray-500">
                No students in this class are currently taught{' '}
                {subjectName(subjectId)} by {teacherName(fromTeacherId)}.
              </p>
            ) : (
              <ul className="text-sm text-gray-600 dark:text-gray-300 grid sm:grid-cols-2 gap-x-4 gap-y-1 max-h-56 overflow-y-auto">
                {affected.map((s) => (
                  <li key={s.id}>{s.fullName}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {msg && (
          <div
            className={`flex items-start gap-2 text-sm rounded-lg p-3 ${
              msg.ok
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {msg.ok ? (
              <Check size={16} className="mt-0.5" />
            ) : (
              <AlertTriangle size={16} className="mt-0.5" />
            )}
            <span>{msg.text}</span>
          </div>
        )}

        {!confirming ? (
          <Button
            icon={<ArrowLeftRight size={16} />}
            disabled={!ready}
            onClick={() => setConfirming(true)}
          >
            Review swap
          </Button>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3">
            <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle size={16} className="mt-0.5" />
              <span>
                Move <strong>{affected.length}</strong> student
                {affected.length === 1 ? '' : 's'} in {cls?.name} from{' '}
                <strong>{teacherName(fromTeacherId)}</strong> to{' '}
                <strong>{teacherName(toTeacherId)}</strong> for{' '}
                <strong>{subjectName(subjectId)}</strong>? This changes the
                current teacher only — prior-term marks and comments are
                untouched.
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" loading={busy} onClick={applySwap}>
                Confirm swap
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
